import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft,
  ChevronRight,
  Edit
} from 'lucide-react';
import styles from './reportCarousel.module.css';
import ReportBlock from '../../../../components/ReportBlock';

export function ReportCarousel({ 
  documents, 
  currentDocumentId, 
  onToggleCarousel,
  onDocumentChange 
}) {
  const [carouselPageIndex, setCarouselPageIndex] = useState(0);
  const [reportBlockExpandedAccounts, setReportBlockExpandedAccounts] = useState({});
  
  // Mock API data - this would come from your actual API
  const realApiData = {
    revenue: 125000,
    expenses: 87500,
    netIncome: 37500,
    cashOnHand: 45000,
    companyName: 'Your Company',
    currentDate: new Date().toLocaleDateString(),
    reportPeriod: 'Current Period'
  };

  const currentDocument = documents.find(doc => doc.id === currentDocumentId);
  const pages = currentDocument?.pages || [];

  // Reset carousel page index when switching documents or pages
  useEffect(() => {
    setCarouselPageIndex(0);
  }, [currentDocumentId]);

  // Make sure carousel page index is valid
  useEffect(() => {
    if (carouselPageIndex >= pages.length && pages.length > 0) {
      setCarouselPageIndex(pages.length - 1);
    }
  }, [pages.length, carouselPageIndex]);

  // Carousel navigation functions
  const nextCarouselPage = () => {
    if (carouselPageIndex < pages.length - 1) {
      setCarouselPageIndex(carouselPageIndex + 1);
    }
  };

  const prevCarouselPage = () => {
    if (carouselPageIndex > 0) {
      setCarouselPageIndex(carouselPageIndex - 1);
    }
  };

  // Keyboard navigation for carousel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && carouselPageIndex > 0) {
        e.preventDefault();
        prevCarouselPage();
      } else if (e.key === 'ArrowRight' && carouselPageIndex < pages.length - 1) {
        e.preventDefault();
        nextCarouselPage();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onToggleCarousel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [carouselPageIndex, pages.length, onToggleCarousel]);

  const goToCarouselPage = (index) => {
    if (index >= 0 && index < pages.length) {
      setCarouselPageIndex(index);
    }
  };

  // Variable rendering functions for carousel
  const getVariableValue = (variableName) => {
    const currentDate = new Date();
    
    switch (variableName) {
      case 'REVENUE':
        return `$${realApiData.revenue?.toLocaleString() || '0'}`;
      case 'EXPENSES':
        return `$${realApiData.expenses?.toLocaleString() || '0'}`;
      case 'NET_INCOME':
        return `$${realApiData.netIncome?.toLocaleString() || '0'}`;
      case 'CASH_ON_HAND':
        return `$${realApiData.cashOnHand?.toLocaleString() || '0'}`;
      case 'COMPANY_NAME':
        return realApiData.companyName || 'Your Company';
      case 'CURRENT_DATE':
        return realApiData.currentDate || new Date().toLocaleDateString();
      case 'REPORT_PERIOD':
        return realApiData.reportPeriod || 'Current Period';
      
      // Cover page variables
      case 'PREPARED_BY':
        return 'Your Accounting Firm';
      case 'REPORT_DATE':
        return currentDate.toLocaleDateString();
      case 'CURRENT_MONTH':
        return currentDate.toLocaleDateString('en-US', { month: 'long' });
      case 'CURRENT_YEAR':
        return currentDate.getFullYear().toString();
      
      default:
        return `{{${variableName}}}`;
    }
  };

  const renderTextWithVariables = (text, mode = 'view') => {
    if (!text) return '';
    
    // Replace {{VARIABLE_NAME}} with actual values (no highlighting in view mode)
    return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
      const value = getVariableValue(variableName);
      // In view mode, never show variable names - if variable not found, show empty string
      return value === `{{${variableName}}}` ? '' : value;
    });
  };

  const renderCarouselBlockContent = (block) => {
    return (
      <ReportBlock 
        key={block.id}
        block={block}
        mode="view"
        reportBlockExpandedAccounts={reportBlockExpandedAccounts}
        setReportBlockExpandedAccounts={setReportBlockExpandedAccounts}
        renderTextWithVariables={renderTextWithVariables}
      />
    );
  };

  if (pages.length === 0) {
    return (
      <div className={styles.carouselContainer}>
        <div className={styles.carouselHeader}>
          <div className={styles.carouselHeaderContent}>
            <h2 className={styles.carouselDocumentTitle}>No Pages Available</h2>
          </div>
          <div className={styles.carouselNavigation}>
            <button
              onClick={onToggleCarousel}
              className={styles.carouselToggleBtn}
              title="Switch to Edit Mode"
            >
              <Edit className={styles.carouselToggleIcon} />
              Edit Mode
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentPage = pages[carouselPageIndex];
  
  return (
    <div className={styles.carouselContainer}>
      <div className={styles.carouselHeader}>
        <div className={styles.carouselHeaderContent}>
          <h2 className={styles.carouselDocumentTitle}>
            {currentDocument?.name || 'Untitled Document'}
          </h2>
          <div className={styles.carouselPageInfo}>
            Page {carouselPageIndex + 1} of {pages.length}
          </div>
          <div className={styles.carouselKeyboardHints}>
            Use ← → arrow keys or Escape to exit
          </div>
        </div>
        <div className={styles.carouselNavigation}>
          <button
            onClick={prevCarouselPage}
            disabled={carouselPageIndex === 0}
            className={styles.carouselNavButton}
            title="Previous page"
          >
            <ChevronLeft className={styles.carouselNavIcon} />
          </button>
          <div className={styles.carouselPageDots}>
            {pages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToCarouselPage(index)}
                className={`${styles.carouselPageDot} ${
                  index === carouselPageIndex ? styles.carouselPageDotActive : ''
                }`}
                title={`Go to page ${index + 1}`}
              />
            ))}
          </div>
          <button
            onClick={nextCarouselPage}
            disabled={carouselPageIndex === pages.length - 1}
            className={styles.carouselNavButton}
            title="Next page"
          >
            <ChevronRight className={styles.carouselNavIcon} />
          </button>
          <button
            onClick={onToggleCarousel}
            className={styles.carouselToggleBtn}
            title="Switch to Edit Mode"
          >
            <Edit className={styles.carouselToggleIcon} />
            Edit Mode
          </button>
        </div>
      </div>

      <div className={styles.carouselContent}>
        <div className={styles.carouselPage}>
          <h1 className={styles.carouselPageTitle}>{currentPage?.title || 'Untitled Page'}</h1>
          <div className={styles.carouselPageBody}>
            {currentPage?.blocks
              ?.filter(block => !block.isPlaceholder)
              .map((block) => (
                <div key={block.id} className={styles.carouselBlock}>
                  {renderCarouselBlockContent(block)}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}